import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import API from "../../../utils/API";
import './SignageMediaLibrary.css';
import DropdownGroup from '../../../components/SignagePlus/DropdownGroup';
import constants from '../../../utils/constants';
import * as modalUtils from '../../../utils/modalUtils';
import useModalInitializationForSites from '../../../hooks/useModalInitializationForSites';

const modalId = 'folderModal';

const AddNewFolder = ({
  setSuccess,
  setError,
  //closeModal,
  loadTable,
  fetchFolderTreeData,
  selectedFolder,
  setSelectedFolder,
  dataMap,
}) => {

  const [formData, setFormData] = useState({
    folderName: ''
  });
  const [modalError, setModalError] = useState('');
  const [folderNameError, setFolderNameError] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [site, setSite] = useState('');
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const [groupData, setGroupData] = useState([]);

  const {
    sites,
    // State
    selectedSiteIds,
    setSiteIds,
    siteDropdownError,
    setSiteDropdownError,    
    // Computed values
    selectedSitesCount,
    selectedSitesText,  
    selectedSiteCmsIds,
    // Handlers
    closeModal,  
    // Helper functions
    generateSiteComponents
  } = useModalInitializationForSites({
    modalId,
    siteExtractor: modalUtils.extractArrayBasedSiteInfo
  });

  useEffect(() => {
    if (selectedFolder) {
      setFormData({
        folderName: selectedFolder.title
      });
      setSiteIds([selectedFolder.siteId]);
    }
  }, [selectedFolder]);

  /**
    * Reset modal
    */
  function resetModal() {
    setSubmitted(false);
    setModalError('');
    setSiteDropdownError('');
    setFolderNameError('');
    setFormData({
      folderName: '',
    });
    setSelectedFolder(null);
  }

  const siteComponents = generateSiteComponents();

  // Reset feature error when modal is shown/hidden
  useEffect(() => {
    const modal = document.getElementById(modalId);
    
    if (modal) {
      const handleModalShow = () => {
          resetModal();
      };
      
      const handleModalHidden = () => {
          resetModal();
      };
      
      modal.addEventListener('show.bs.modal', handleModalShow);
      modal.addEventListener('hidden.bs.modal', handleModalHidden);
      
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
        modal.removeEventListener('hidden.bs.modal', handleModalHidden);
      };
    }
  }, []);

  useEffect(() => {
    if (selectedSiteIds.length === 0) {
      setSiteDropdownError(<FormattedMessage id="NO_SITES_SELECTED" />);
    } else {
      setSiteDropdownError(''); 
    }
    if(siteDropdownError ==='' && folderNameError ===''){
      setModalError('');
    }
  }, [selectedSiteIds, formData]);

  /**
   * Handle form input change
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === 'folderName' && (value.length < 2 || value.length > 36)) {
      setFolderNameError(<FormattedMessage id="GROUP_NAME_LENGTH" />);
    } else {
      setFolderNameError('');
    }
  }, []);

  /**
   * Add new group to the database
   */
  const handleAddNewFolder = async () => {
    const folderData = {
      folderName: formData.folderName.trim(),
    };
    if (folderData.folderName.length < 2 || folderData.folderName.length > 36) {
      setFolderNameError(<FormattedMessage id="FOLDER_NAME_LENGTH" />);
      return;
    }
    try {
      //await addFolder(folderData.folderName);
      const mediaData = {
        data:{
          category: constants.SIGNAGE_MEDIA.MEDIA_CATEGORY.MEDIA,
          subcategory: constants.SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.FOLDER,
          type: constants.SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.FOLDER,
          parentId: null,
          name: formData.folderName.trim(),
          cmsId: selectedSiteCmsIds.length > 0 ? selectedSiteCmsIds[0] : null,
          siteId: selectedSiteIds.length > 0 ? selectedSiteIds[0] : null
        }
      };
      let res = await API.insertMediaFolder(mediaData);
      if(res.status === 200){
        setSuccess(<FormattedMessage id="ADD_FOLDER_SUCCESS" />);
      }else{
        setError(<FormattedMessage id="ADD_FOLDER_FAILED" values={{ errorMsg: res.status }}/>);
      }

      if (typeof fetchFolderTreeData === 'function') fetchFolderTreeData();
      if (typeof loadTable === 'function') loadTable(dataMap);
      closeModal(/*modalId*/);
      resetModal();
    } catch (err) {
      let message = err?.response?.data?.message;
      message = message? t(message) : err.message;
      setModalError(<FormattedMessage id='ADD_FOLDER_FAILED' values={{ errorMsg: <b>{message}</b> }} />);
    }
  };

  const handleEditFolder = async () => {
    const folderData = {
      folderName: formData.folderName.trim(),
    };
    if (folderData.folderName.length < 2 || folderData.folderName.length > 36) {
      setFolderNameError(<FormattedMessage id="FOLDER_NAME_LENGTH" />);
      return;
    }
    try {
      const mediaData = {
        data:{
          /*category: constants.SIGNAGE_MEDIA.MEDIA_CATEGORY.MEDIA,
          subcategory: constants.SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.FOLDER,
          type: constants.SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.FOLDER,
          parentId: null,*/
          name: formData.folderName.trim(),
          cmsId: selectedSiteCmsIds.length > 0 ? selectedSiteCmsIds[0] : null,
          siteId: selectedSiteIds.length > 0 ? selectedSiteIds[0] : null
        }
      };
      let res = await API.updateMediaFolder(selectedFolder.key, mediaData);
      if(res.status === 200){
        setSuccess(<FormattedMessage id="EDIT_FOLDER_SUCCESS" />);
      }else{
        setError(<FormattedMessage id="EDIT_FOLDER_FAILED" values={{ errorMsg: res.status }}/>);
      }

      if (typeof fetchFolderTreeData === 'function') fetchFolderTreeData();
      if (typeof loadTable === 'function') loadTable(dataMap);
      closeModal(/*modalId*/);
      resetModal();
    } catch (err) {
      let message = err?.response?.data?.message;
      message = message? t(message) : err.message;
      setModalError(<FormattedMessage id="EDIT_FOLDER_FAILED" values={{ errorMsg: <b>{message}</b> }} />);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!formData.folderName) {
      setFolderNameError([<FormattedMessage id="EMPTY_FIELD_ERROR" />, <FormattedMessage id={'FOLDER_NAME'} />]);
    }
    if (selectedSiteIds.length === 0) {
       setSiteDropdownError(<FormattedMessage id="NO_SITES_SELECTED" />);
    }

    if (!folderNameError && !siteDropdownError/*formData.folderName && selectedSiteIds.length > 0*/ ) {
      if (selectedFolder) {
        handleEditFolder();
      } else {
        handleAddNewFolder();
      }

    } else {
      setModalError(<FormattedMessage id="SUBMIT_ERROR" />);
    }
  };

  return (
    <div className='modal modal-mse fade' id={modalId} tabIndex='-1' aria-labelledby='folderModalLabel' aria-hidden='true'>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content modal-mes-content' style={{ overflow: 'visible' }}>
          <div className='modal-header modal-mes-header' style={{ backgroundColor: '#347BFA' }}>
            <h5 className='modal-title modal-mes-title'><FormattedMessage id='ADD_NEW_FOLDER' /></h5>
            <button id="closeModal" type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
          </div>
          <div className='modal-body' style={{ overflow: 'visible' }}>
            <form onSubmit={handleSubmit}>
              <div className='row' >
                <div className='folderNameSection mb-2'>
                  <label htmlFor='folderNameLabel' className='font-weight-600 font-size-14 mb-2'>
                    <FormattedMessage id={'FOLDER_NAME'} />:
                  </label>
                  <input
                    id="folderNameInput"
                    onChange={handleChange}
                    value={formData.folderName}
                    name='folderName'
                    placeholder={t('FOLDER_NAME')}
                    className={`form-control h-54 bg-baby-blue ${submitted && (folderNameError ? 'is-invalid' : 'is-valid')}`}
                  />
                  {/*submitted &&*/ folderNameError && (
                  <div className='invalid-feedback'>
                    {folderNameError}
                  </div>
                )}
                </div>
              </div>
              <div className='row mt-3' >
                <div className="mb-3">
                  <label className="font-weight-600 font-size-14 mb-2" htmlFor='btnSiteDrop' ><FormattedMessage id="SELECT_SITE" />:</label>
                  <DropdownGroup
                    options={siteComponents}
                    value={site}
                    onChange={(value) => setSite(value)}
                    text="SELECT_SITE"
                    selectedSitesText={selectedSitesText}
                    selectedSitesCount={selectedSitesCount}
                    className={`form-control h-54 bg-baby-blue ${submitted && (siteDropdownError ? 'is-invalid' : 'is-valid')}`}
                  />
                  {submitted && siteDropdownError && (
                    <div className='d-block invalid-feedback'>
                      {siteDropdownError}
                    </div>
                  )}
                </div>
                {modalError && (
                  <div className='modal-error invalid-feedback d-block fw-bold text-center'>
                    {modalError}
                  </div>
                )}
              </div>              
            </form>
          </div>
          <div className='modal-footer modal-mes-footer d-flex align-items-center'>
            <button id="backGroupModalBtn" type='button' className='btn btn-outline-secondary me-3' data-bs-dismiss='modal'>
              <FormattedMessage id='CANCEL' />
            </button>
            <button id="saveGroupBtn" className='btn btn-primary but-darkblue' type='button' onClick={handleSubmit}>
              <FormattedMessage id='CONTINUE' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewFolder;
